import { apiClient, getApiUrl } from '../lib/apiClient';
import type { 
  Class,
  Student,
  CreateClassRequest,
  UpdateClassRequest,
  ListQueryParams,
  ApiResponse,
  Pagination
} from '../types/api';
import { 
  extractListData, 
  extractSingleData, 
  extractPagination,
  type RailsWrappedListResponse,
  type RailsSingleWrappedResponse 
} from '../utils/railsResponseHandler';

// Class list response based on OpenAPI
interface ClassListResponse {
  classes: Class[];
  pagination: Pagination;
}

// Class student operations request/response types
interface AddStudentRequest {
  student_id: string;
}

interface RemoveStudentRequest {
  student_id: string;
}

interface ClassStudentsResponse {
  students: Student[];
  total_count: number;
}

// Get all classes for a school
export const getClasses = async (
  schoolId: string, 
  params?: ListQueryParams
): Promise<ClassListResponse> => {
  const response = await apiClient.get<RailsWrappedListResponse<Class>>(
    getApiUrl(`/schools/${schoolId}/classes`), 
    { params }
  );
  
  const classes = extractListData<Class>(response.data);
  const pagination = extractPagination(response.data) || {
    current_page: 1,
    total_pages: 1,
    total_count: classes.length,
    per_page: 20,
    has_previous: false,
    has_next: false
  };
  
  return { classes, pagination };
};

// Create a new class
export const createClass = async (
  schoolId: string,
  data: CreateClassRequest
): Promise<Class> => {
  const response = await apiClient.post<RailsSingleWrappedResponse<Class>>(
    getApiUrl(`/schools/${schoolId}/classes`),
    data
  );
  return extractSingleData<Class>(response.data);
};

// Get a specific class
export const getClass = async (
  schoolId: string,
  classId: string
): Promise<Class> => {
  const response = await apiClient.get<RailsSingleWrappedResponse<Class>>(
    getApiUrl(`/schools/${schoolId}/classes/${classId}`)
  );
  return extractSingleData<Class>(response.data);
};

// Update a class
export const updateClass = async (
  schoolId: string,
  classId: string,
  data: UpdateClassRequest
): Promise<Class> => {
  const response = await apiClient.put<RailsSingleWrappedResponse<Class>>(
    getApiUrl(`/schools/${schoolId}/classes/${classId}`),
    data
  );
  return extractSingleData<Class>(response.data);
};

// Delete a class
export const deleteClass = async (
  schoolId: string,
  classId: string
): Promise<void> => {
  await apiClient.delete(
    getApiUrl(`/schools/${schoolId}/classes/${classId}`)
  );
};

// Get students in a class
export const getClassStudents = async (
  schoolId: string,
  classId: string,
  params?: ListQueryParams
): Promise<ClassStudentsResponse> => {
  const response = await apiClient.get<RailsWrappedListResponse<Student>>(
    getApiUrl(`/schools/${schoolId}/classes/${classId}/students`),
    { params }
  );
  
  const students = extractListData<Student>(response.data);
  
  return { 
    students, 
    total_count: (response.data.meta?.pagination && response.data.meta.total_count) || students.length 
  };
};

// Add a student to a class
export const addStudentToClass = async (
  schoolId: string,
  classId: string,
  studentId: string
): Promise<ApiResponse<{ message: string }>> => {
  const response = await apiClient.post(
    getApiUrl(`/schools/${schoolId}/classes/${classId}/add_student`),
    { student_id: studentId } as AddStudentRequest
  );
  return response.data;
};

// Remove a student from a class
export const removeStudentFromClass = async (
  schoolId: string,
  classId: string,
  studentId: string
): Promise<ApiResponse<{ message: string }>> => {
  const response = await apiClient.post(
    getApiUrl(`/schools/${schoolId}/classes/${classId}/remove_student`),
    { student_id: studentId } as RemoveStudentRequest
  );
  return response.data;
};